import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatValidUntil,
  pluralCredits,
  type CreditsResponse,
  type PackDto,
  type Pricing,
  type ProfileResponse,
} from '@app/shared';
import { HelpHint } from '../components/HelpHint';
import { PurchaseSheet } from '../components/PurchaseSheet';
import { Card, Skeleton, SkeletonGroup } from '../components/ui';
import { useT } from '../i18n';
import { api } from '../lib/api';
import { creditsCount, priceRows } from '../lib/format';
import { paymentOptionsQuery } from '../lib/payment-options';
import { isForever, packPriceLine } from '../lib/purchase';

function SectionLabel({ children }: { children: ReactNode }) {
  return <h2 className="text-xs font-bold uppercase tracking-[0.06em] text-hint mb-2 mt-5">{children}</h2>;
}

/** «Кредиты» (spec 6.1): the balance, the packs on sale, where the credits came from and what things cost. */
export function CreditsPage() {
  const t = useT();
  const qc = useQueryClient();

  // The balance may have changed since the app was opened (monthly grant, spending).
  useEffect(() => {
    api
      .getProfile()
      .then((p) => qc.setQueryData<ProfileResponse>(['profile'], p))
      .catch(() => {});
  }, [qc]);

  const { data: profile } = useQuery<ProfileResponse>({ queryKey: ['profile'], enabled: false });
  const creditsQuery = useQuery<CreditsResponse>({
    queryKey: ['credits'],
    queryFn: () => api.getCredits(),
  });
  const pricingQuery = useQuery<Pricing>({
    queryKey: ['pricing'],
    queryFn: () => api.getPricing(),
    staleTime: 5 * 60_000,
  });
  // Nothing on sale (every method off, or no pack) — no «Купить» block at all.
  const optionsQuery = useQuery(paymentOptionsQuery);
  const packs = optionsQuery.data?.packs ?? [];
  const [buying, setBuying] = useState<PackDto | null>(null);

  const balance = profile?.user.balanceCredits ?? 0;
  const next = profile?.user.creditsNextExpiry ?? null;
  const lots = creditsQuery.data?.lots ?? [];
  const pricing = pricingQuery.data;

  if (creditsQuery.isLoading || pricingQuery.isLoading) {
    return (
      <div className="pt-2">
        <h1 className="flex items-center gap-2 text-xl font-extrabold mb-4">
        {t.credits.title}
        <HelpHint topic="credits" />
      </h1>
        <SkeletonGroup label={t.common.loading} className="space-y-3">
          <Skeleton className="h-28" />
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
        </SkeletonGroup>
      </div>
    );
  }

  return (
    <div className="pt-2">
      <h1 className="flex items-center gap-2 text-xl font-extrabold mb-4">
        {t.credits.title}
        <HelpHint topic="credits" />
      </h1>

      <Card variant="surface" padding="none" className="p-5">
        <p className="text-xs font-bold uppercase tracking-[0.06em] text-hint mb-1">{t.credits.balance}</p>
        <p className="mb-2">
          <span className="text-3xl font-extrabold tabular-nums">{balance}</span>
          <span className="text-base font-medium text-hint ml-2">{pluralCredits(balance)}</span>
        </p>
        {!next ? (
          <p className="text-sm text-hint">{t.credits.empty}</p>
        ) : isForever(next.expiresAt) ? (
          <p className="text-sm text-hint">{t.credits.forever}</p>
        ) : next.credits === balance ? (
          <p className="text-sm text-hint">{t.credits.validUntilAll(formatValidUntil(next.expiresAt))}</p>
        ) : (
          <p className="text-sm text-warning">
            {t.credits.burnSoon(creditsCount(next.credits), formatValidUntil(next.expiresAt))}
          </p>
        )}
      </Card>

      {packs.length > 0 && (
        <>
          <SectionLabel>{t.purchase.title}</SectionLabel>
          <ul className="space-y-2">
            {packs.map((pack) => (
              <li key={pack.id}>
                <button
                  type="button"
                  onClick={() => setBuying(pack)}
                  className="w-full rounded-xl bg-surface shadow-card p-4 text-left transition-[transform,opacity] duration-150 ease-smooth active:scale-[0.99] active:opacity-80"
                >
                  <span className="flex items-baseline justify-between gap-3">
                    <span className="font-bold">{pack.title}</span>
                    <span className="text-sm text-hint tabular-nums whitespace-nowrap">{creditsCount(pack.credits)}</span>
                  </span>
                  <span className="block mt-1 text-sm font-semibold tabular-nums">{packPriceLine(pack, t)}</span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      <SectionLabel>{t.credits.lotsTitle}</SectionLabel>
      <Card variant="surface" padding="none" className="px-4 py-1">
        {creditsQuery.isError && <p className="text-sm text-hint py-3">{t.common.failed}</p>}
        {creditsQuery.data && lots.length === 0 && <p className="text-sm text-hint py-3">{t.credits.empty}</p>}
        {lots.length > 0 && (
          <ul className="divide-y divide-line">
            {lots.map((l, i) => (
              <li key={i} className="flex items-center justify-between gap-3 py-3 text-sm">
                <span>{t.credits.sources[l.source]}</span>
                <span className="tabular-nums text-hint whitespace-nowrap">
                  {isForever(l.expiresAt)
                    ? t.credits.lotLineForever(l.remaining, l.amount)
                    : t.credits.lotLine(l.remaining, l.amount, formatValidUntil(l.expiresAt))}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <SectionLabel>{t.credits.pricesTitle}</SectionLabel>
      <Card variant="surface" padding="none" className="px-4 py-1">
        {pricingQuery.isError && <p className="text-sm text-hint py-3">{t.common.failed}</p>}
        {pricing && (
          <ul className="divide-y divide-line">
            {priceRows(pricing, t).map((row) => (
              <li key={row.label} className="flex items-center justify-between gap-3 py-3 text-sm">
                <span>{row.label}</span>
                <span className="tabular-nums font-semibold whitespace-nowrap">{row.price}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <div className="mt-2 space-y-1 px-1">
        {pricing && <p className="text-xs text-hint">{t.credits.freeRebuilds(pricing.freeRebuilds)}</p>}
        <p className="text-xs text-hint">{t.credits.monthlyNote}</p>
      </div>

      <PurchaseSheet
        pack={buying}
        email={optionsQuery.data?.email ?? null}
        offerUrl={optionsQuery.data?.offerUrl ?? '/offer.html'}
        onClose={() => setBuying(null)}
      />
    </div>
  );
}
